import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { grade, isLocallyGradable, type GradeResult } from '@shared/grading'
import {
  advance,
  currentItem,
  FINAL_ROUND_SIZE,
  initPractice,
  markSolid,
  remaining,
  requeue,
  type PracticeState,
  type RepeatReason,
} from '@shared/practice'
import type { ExerciseResponse } from '@shared/schemas/exercise'
import { Button } from '@web/components/ui/Button'
import { useCompleteLesson, useSaveLessonProgress, useSubmitAttempt } from '@web/lib/attempts'
import { AudioProvider, SpeakButton } from '@web/lib/audio'
import { useLesson } from '@web/lib/catalog'
import { playLessonComplete, playResult } from '@web/lib/sound'
import { ContentBlockView } from './ContentBlockView'
import { DeferredExercise } from './exercises/DeferredExercise'
import { SpeakingExercise } from './exercises/SpeakingExercise'
import { FillBlankExercise } from './exercises/FillBlankExercise'
import { ListeningExercise } from './exercises/ListeningExercise'
import { MatchingExercise } from './exercises/MatchingExercise'
import { MultipleChoiceExercise } from './exercises/MultipleChoiceExercise'
import { TranslationExercise } from './exercises/TranslationExercise'
import { hasAnswer } from './exercises/types'
import { WordOrderExercise } from './exercises/WordOrderExercise'
import { buildSteps, countExercises, type LessonStep } from './steps'

/**
 * Plays a lesson.
 *
 * Grading happens here, on-device, using the same module the Worker runs. That
 * is what makes feedback instant and keeps a lesson usable with no signal.
 *
 * The order of play comes from `@shared/practice` rather than from the step
 * list directly: anything she gets wrong comes back later in the same lesson,
 * and the lesson closes with a short round of what she got right. Seeing a word
 * exactly once was the single biggest reason lessons did not stick.
 */
export function LessonPlayer() {
  const { lessonId = '' } = useParams()
  const navigate = useNavigate()
  const { data: lesson, isPending, error } = useLesson(lessonId)

  const steps = useMemo(() => (lesson ? buildSteps(lesson) : []), [lesson])
  const [practice, setPractice] = useState<PracticeState<LessonStep> | null>(null)
  const [response, setResponse] = useState<ExerciseResponse | null>(null)
  const [result, setResult] = useState<GradeResult | null>(null)
  const [scores, setScores] = useState<Record<string, number>>({})

  const view = practice ? currentItem(practice) : null
  const step = view?.item
  const totalExercises = countExercises(steps)

  // Only repeatable work can be projected into the closing round: speaking is
  // graded on the server and asking her to re-record is a different, heavier
  // request than re-tapping an answer.
  const projectedFinalRound = useMemo(
    () =>
      Math.min(
        FINAL_ROUND_SIZE,
        steps.filter((s) => s.kind === 'exercise' && isLocallyGradable(s.exercise.content.type))
          .length,
      ),
    [steps],
  )

  const submitAttempt = useSubmitAttempt()
  const completeLesson = useCompleteLesson(lessonId, lesson?.courseId)
  const saveProgress = useSaveLessonProgress(lessonId)
  const startedAt = useRef(Date.now())
  const shownAt = useRef(Date.now())
  const completionSent = useRef(false)
  const resumeApplied = useRef(false)
  const [resumed, setResumed] = useState(false)

  /**
   * Open the queue, at where she stopped last time.
   *
   * Runs once per mount, guarded by a ref rather than by comparing state — the
   * lesson query can resolve more than once (cache hydration, refetch) and
   * re-applying the saved position would yank her backwards mid-lesson.
   *
   * A lesson she has already finished always restarts at the top: opening a
   * completed lesson means reviewing it, not resuming it. Repeats are never
   * restored — `last_position` is a position in the authored spine, and the
   * queue rebuilds itself from her answers this time round.
   */
  useEffect(() => {
    if (!lesson || resumeApplied.current) return
    resumeApplied.current = true

    // Clamped inside `initPractice`, because content can be re-authored after a
    // position is saved and leave a stored index that no longer exists.
    const target = lesson.status === 'completed' ? 0 : lesson.lastPosition
    setPractice(initPractice(steps, target))
    if (target > 0) setResumed(true)
  }, [lesson, steps])

  // Timing each exercise separately feeds the "weak areas" report later —
  // a question answered correctly but slowly is not the same as one she knows.
  useEffect(() => {
    shownAt.current = Date.now()
  }, [view?.item.key, view?.occurrence])

  // Kept as the best score per exercise, matching how the server tallies the
  // lesson: a repeat can only ever raise what a miss cost her.
  const recordResult = useCallback((exerciseId: string, score: number) => {
    setScores((prev) => ({ ...prev, [exerciseId]: Math.max(prev[exerciseId] ?? 0, score) }))
  }, [])

  function check() {
    if (!step || step.kind !== 'exercise' || !response || !view || !practice) return
    const graded = grade(step.exercise.content, response)
    setResult(graded)
    recordResult(step.exercise.id, graded.score)

    // A miss goes back into the queue; a first-time hit becomes a candidate for
    // the closing round. Both are recorded now rather than on Continue, so the
    // decision uses the answer she actually gave.
    setPractice((p) => (p ? (graded.correct ? markSolid(p, view) : requeue(p, view)) : p))

    // Played from inside the click handler: browsers only allow an
    // AudioContext to start from a user gesture, and this is that gesture.
    playResult(graded)

    // Feedback is already on screen; persistence happens behind it.
    submitAttempt.mutate({
      attemptId: crypto.randomUUID(),
      exerciseId: step.exercise.id,
      response,
      durationMs: Date.now() - shownAt.current,
      clientCreatedAt: Date.now(),
    })
  }

  function next() {
    if (!practice || !view) return
    setResponse(null)
    setResult(null)
    setResumed(false)
    setPractice(advance(practice))

    // Computed outside the state updater on purpose: React may invoke an
    // updater twice in StrictMode, which would fire this request twice.
    //
    // Only spine steps move the saved position, and it is saved on every one
    // of them — teaching blocks submit no attempt, so without this a lesson
    // read halfway through would resume from the beginning. Repeats sit
    // outside the spine and must not shift it.
    const spineNext = practice.spineIndex + 1
    if (view.fromSpine && spineNext < steps.length) {
      saveProgress.mutate({ lastPosition: spineNext })
    }
  }

  // Fires once, when the queue drains. A ref rather than state because a
  // re-render must not be able to submit the completion twice.
  const reachedEnd = practice !== null && steps.length > 0 && view === null
  useEffect(() => {
    if (!reachedEnd || completionSent.current) return
    completionSent.current = true
    playLessonComplete()
    completeLesson.mutate({ timeSpentMs: Date.now() - startedAt.current })
  }, [reachedEnd, completeLesson])

  if (isPending || (lesson && !practice)) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <span
          aria-label="Loading lesson"
          className="size-8 animate-spin rounded-full border-3 border-brand-600 border-t-transparent"
        />
      </div>
    )
  }

  if (error || !lesson) {
    return (
      <Centered>
        <p className="text-ink-700">This lesson could not be opened.</p>
        <Button onClick={() => void navigate('/')}>Back to course</Button>
      </Centered>
    )
  }

  if (!step) {
    const saved = completeLesson.data

    // Prefer the server's tally. It counts every attempt she has ever made on
    // this lesson, so a lesson resumed halfway through reports the whole
    // score rather than only what she answered in this sitting. Local numbers
    // are the offline fallback.
    const localEarned = Object.values(scores).reduce((sum, s) => sum + s, 0)
    const answered = saved?.answered ?? Object.keys(scores).length
    const total = saved?.total ?? totalExercises
    const pct =
      saved?.scorePct ?? (totalExercises === 0 ? 0 : Math.round((localEarned / totalExercises) * 100))

    return (
      <Centered>
        <p className="text-6xl" aria-hidden="true">
          {pct >= 80 ? '🎉' : '🌱'}
        </p>
        <h1 className="font-display text-3xl font-bold">{lesson.title}</h1>
        <p className="text-ink-700">
          {answered} of {total} answered · {pct}% correct
        </p>
        <p className="text-sm text-ink-500">
          {pct >= 80
            ? 'You are one step closer to Brazil.'
            : 'Every round makes the next one easier.'}
        </p>

        {saved && (
          <div className="flex gap-3">
            {saved.xpEarned > 0 && (
              <span className="rounded-full bg-brand-100 px-3 py-1 text-sm font-semibold text-brand-700">
                +{saved.xpEarned} XP
              </span>
            )}
            {saved.currentStreak > 0 && (
              <span className="rounded-full bg-gold-200 px-3 py-1 text-sm font-semibold text-gold-700">
                🔥 {saved.currentStreak} day{saved.currentStreak === 1 ? '' : 's'}
              </span>
            )}
          </div>
        )}

        {completeLesson.isError && (
          <p role="status" className="text-sm text-ink-500">
            Saved on this device — it will sync when you are back online.
          </p>
        )}

        <Button size="lg" onClick={() => void navigate('/')}>
          Back to course
        </Button>
      </Centered>
    )
  }

  const isExercise = step.kind === 'exercise'
  const canCheck = isExercise && hasAnswer(response)
  const gradable = isExercise ? isLocallyGradable(step.exercise.content.type) : false

  // The denominator moves as she works: a miss adds a step. Counting the
  // closing round from the start means the bar only ever slows down, never
  // slides backwards, which is what makes a growing queue feel fair.
  const left = practice ? remaining(practice, projectedFinalRound) : 0
  const passed = practice?.done ?? 0
  const barTotal = Math.max(passed + left, 1)

  return (
    <AudioProvider audio={lesson.audio}>
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-10 bg-sand-50/90 px-4 pt-[max(0.75rem,var(--safe-top))] pb-3 backdrop-blur">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button
            type="button"
            onClick={() => void navigate('/')}
            aria-label="Leave lesson"
            className="min-h-11 min-w-11 rounded-full text-ink-500 hover:bg-sand-200"
          >
            ✕
          </button>
          <div
            className="h-2.5 flex-1 overflow-hidden rounded-full bg-sand-200"
            role="progressbar"
            aria-valuenow={passed}
            aria-valuemin={0}
            aria-valuemax={barTotal}
            aria-label="Lesson progress"
          >
            <div
              className="h-full rounded-full bg-brand-600 transition-[width] duration-300"
              style={{ width: `${(passed / barTotal) * 100}%` }}
            />
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-lg flex-1 px-4 py-4">
        {resumed && (
          <p
            role="status"
            className="mb-4 rounded-xl bg-sand-100 px-4 py-2.5 text-sm text-ink-700"
          >
            Picking up where you left off.
          </p>
        )}
        {isExercise ? (
          <ExercisePane
            // Remounts on every repeat. Without it a re-asked exercise would
            // reopen holding the taps she made the first time round.
            key={`${step.key}:${view?.occurrence ?? 0}`}
            step={step}
            repeat={view?.reason ?? null}
            response={response}
            onChange={setResponse}
            locked={result !== null}
            result={result}
            onSpoken={(exerciseId, passed) => recordResult(exerciseId, passed ? 1 : 0)}
          />
        ) : (
          <ContentBlockView block={step.block.block} />
        )}
      </main>

      <footer className="sticky bottom-0 border-t border-sand-200 bg-white px-4 pt-3 pb-[max(0.75rem,var(--safe-bottom))]">
        <div className="mx-auto max-w-lg space-y-3">
          {result && <Feedback result={result} />}

          {!isExercise || !gradable ? (
            <Button size="lg" className="w-full" onClick={next}>
              Continue
            </Button>
          ) : result ? (
            <Button size="lg" className="w-full" onClick={next}>
              Continue
            </Button>
          ) : (
            <Button size="lg" className="w-full" onClick={check} disabled={!canCheck}>
              Check
            </Button>
          )}
        </div>
      </footer>
    </div>
    </AudioProvider>
  )
}

function ExercisePane({
  step,
  repeat,
  response,
  onChange,
  locked,
  result,
  onSpoken,
}: {
  step: Extract<LessonStep, { kind: 'exercise' }>
  /** Set when this is a second look rather than a first meeting. */
  repeat: RepeatReason | null
  response: ExerciseResponse | null
  onChange: (r: ExerciseResponse) => void
  locked: boolean
  result: GradeResult | null
  /** Speaking is graded on the server, so it reports its own verdict. */
  onSpoken: (exerciseId: string, passed: boolean) => void
}) {
  const { content } = step.exercise

  return (
    <div className="space-y-5">
      {/*
        A repeat says so, in gold rather than mint. She should know the app is
        circling back on purpose — an unexplained repeat reads as a bug, and
        "you saw this one already" is also the sentence that makes the second
        look feel like recall rather than a fresh question.
      */}
      {repeat ? (
        <p className="text-xs font-semibold tracking-[0.16em] text-gold-700 uppercase">
          {repeat === 'again' ? 'One more look' : 'Before you go'}
        </p>
      ) : (
        step.activityTitle && (
          <p className="text-xs font-semibold tracking-[0.16em] text-brand-500 uppercase">
            {step.activityTitle}
          </p>
        )
      )}
      <h2 className="text-xl font-semibold text-ink-900">{step.exercise.prompt}</h2>
      {step.instructions && <p className="text-sm text-ink-500">{step.instructions}</p>}

      {/*
        The switch is exhaustive over the exercise-type union: adding a type to
        the schema makes TypeScript fail here until a renderer exists.
      */}
      {content.type === 'multiple_choice' && (
        <MultipleChoiceExercise
          content={content}
          response={response?.type === 'multiple_choice' ? response : null}
          onChange={onChange}
          locked={locked}
          result={result}
        />
      )}
      {content.type === 'fill_blank' && (
        <FillBlankExercise
          content={content}
          response={response?.type === 'fill_blank' ? response : null}
          onChange={onChange}
          locked={locked}
          result={result}
        />
      )}
      {content.type === 'translation' && (
        <TranslationExercise
          content={content}
          response={response?.type === 'translation' ? response : null}
          onChange={onChange}
          locked={locked}
          result={result}
        />
      )}
      {content.type === 'word_order' && (
        <WordOrderExercise
          content={content}
          response={response?.type === 'word_order' ? response : null}
          onChange={onChange}
          locked={locked}
          result={result}
        />
      )}
      {content.type === 'matching' && (
        <MatchingExercise
          content={content}
          response={response?.type === 'matching' ? response : null}
          onChange={onChange}
          locked={locked}
          result={result}
        />
      )}
      {content.type === 'listening' && (
        <ListeningExercise
          content={content}
          response={response?.type === 'listening' ? response : null}
          onChange={onChange}
          locked={locked}
          result={result}
        />
      )}
      {content.type === 'speaking' && (
        <SpeakingExercise
          exerciseId={step.exercise.id}
          content={content}
          // Speaking is graded server-side against a transcript, so it reports
          // its own result rather than going through the local grader.
          onResult={(spoken) => onSpoken(step.exercise.id, spoken.feedback.passed)}
        />
      )}
      {content.type === 'conversation' && <DeferredExercise content={content} />}
    </div>
  )
}

/**
 * A miss is never framed as failure.
 *
 * The brand voice rules this out explicitly, and it is also just better
 * teaching: "Not quite" tells her she lost, "Almost — here's the way to say
 * it" tells her what to do next. The colour follows the same logic — mint and
 * gold rather than green and red, so a wrong answer does not feel like an
 * alarm going off.
 */
function Feedback({ result }: { result: GradeResult }) {
  const tone = result.correct
    ? 'bg-brand-100 text-brand-800'
    : result.score > 0
      ? 'bg-gold-100 text-gold-700'
      : 'bg-cream text-ink-700 ring-1 ring-gold-300'

  const heading = result.correct
    ? 'Isso mesmo!'
    : result.score > 0
      ? 'Quase lá — almost!'
      : 'Almost! Try it this way.'

  return (
    <div role="status" aria-live="polite" className={`rounded-xl px-4 py-3 ${tone}`}>
      <p className="font-display font-bold">{heading}</p>
      {result.feedback.message && <p className="mt-0.5 text-sm">{result.feedback.message}</p>}
      {result.feedback.expected && !result.correct && (
        <p className="mt-1 flex items-center gap-2 text-sm">
          <span>
            Say it like this:{' '}
            <span lang="pt-BR" className="font-semibold">
              {result.feedback.expected}
            </span>
          </span>
          {/* Audio for an answer appears only now, once she has committed —
              hearing it beforehand would give the question away. */}
          <SpeakButton text={result.feedback.expected} size="sm" />
        </p>
      )}
      {result.feedback.explanation && !result.correct && (
        <p className="mt-1 text-sm">{result.feedback.explanation}</p>
      )}
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
      {children}
    </main>
  )
}
